import i18n, { type TFunction } from "i18next";
import { iriLocalName } from "../../shared/references/shortReferences";
import { requestJson } from "../http/httpClient";
import { readArray, readBoolean, readNullableString, readObject, readString, readStringRecord } from "../http/jsonDecoding";

/**
 * The words of the DMW process ontology's terms, as the server serves them from
 * `dmw-process.ttl` (V23, AR-07): each term's labels and definitions, and the class of each
 * item kind and step kind. The web app keeps no copy. It reads them once and holds them in
 * i18next as two namespaces keyed by IRI, so a view names a term in the user's language with
 * English as the fallback, and re-renders when they arrive (`bindI18nStore: "added"`).
 * `RequireSession` reads them once signed in; before they arrive, a caller shows its own fallback.
 */

export interface TaggedText {
  readonly text: string;
  readonly language: string | null;
}

export interface ProcessTermWords {
  readonly iri: string;
  readonly labels: readonly TaggedText[];
  readonly definitions: readonly TaggedText[];
}

export interface ProcessTerms {
  readonly core: string;
  readonly terms: readonly ProcessTermWords[];
  readonly itemKinds: Readonly<Record<string, string>>;
  readonly stepKinds: Readonly<Record<string, string>>;
  /** What each step kind alone implies on the two step axes, as module B's table says it (WD-1+3). */
  readonly stepKindAxes: Readonly<Record<string, KindAxes>>;
}

/**
 * What a step's kind alone says on Johannes's two axes (30 Oct): a person answers every step of
 * the kind, or its placement decides (`null`); an LLM carries out part of every step of the kind.
 * A kind's mark and the legend say it; a card marks only what its kind doesn't.
 */
export interface KindAxes {
  readonly needsPerson: boolean | null;
  readonly usesLlm: boolean;
}

const LABELS = "processTermLabels";
const DEFINITIONS = "processTermDefinitions";
const BY_IRI = { keySeparator: false, nsSeparator: false } as const;

let kindClasses: Pick<ProcessTerms, "itemKinds" | "stepKinds" | "stepKindAxes"> = { itemKinds: {}, stepKinds: {}, stepKindAxes: {} };
let loading: Promise<void> | null = null;

export function readProcessTerms(value: unknown): ProcessTerms {
  const body = readObject(value, "process terms");
  const readTexts = (item: unknown) => {
    const text = readObject(item, "tagged text");
    return { text: readString(text, "text"), language: readNullableString(text, "language") };
  };
  return {
    core: readString(body, "core"),
    terms: readArray(body["terms"], "process terms", (item) => {
      const term = readObject(item, "process term");
      return {
        iri: readString(term, "iri"),
        labels: readArray(term["labels"], "labels", readTexts),
        definitions: readArray(term["definitions"], "definitions", readTexts),
      };
    }),
    itemKinds: readStringRecord(body, "item_kinds"),
    stepKinds: readStringRecord(body, "step_kinds"),
    stepKindAxes: Object.fromEntries(
      Object.entries(readObject(body["step_kind_axes"], "step kind axes")).map(([kind, value]) => {
        const axes = readObject(value, "step kind axes");
        return [kind, { needsPerson: axes["needs_person"] === null ? null : readBoolean(axes, "needs_person"), usesLlm: readBoolean(axes, "uses_llm") }];
      }),
    ),
  };
}

/** Holds the served words; a language-less text counts as English. */
export function holdProcessTerms(terms: ProcessTerms): void {
  kindClasses = { itemKinds: terms.itemKinds, stepKinds: terms.stepKinds, stepKindAxes: terms.stepKindAxes };
  holdWords(terms.terms);
}

/** Adds the words of a few terms to those held, keeping theirs: an invitation's preview carries
 * the words for the kinds its holder will curate, before they sign in and read all of them (F133). */
export function addProcessTerms(terms: ProcessTerms): void {
  kindClasses = {
    itemKinds: { ...terms.itemKinds, ...kindClasses.itemKinds },
    stepKinds: { ...terms.stepKinds, ...kindClasses.stepKinds },
    stepKindAxes: { ...terms.stepKindAxes, ...kindClasses.stepKindAxes },
  };
  holdWords(terms.terms);
}

function holdWords(terms: readonly ProcessTermWords[]): void {
  for (const [namespace, texts] of [
    [LABELS, (term: ProcessTermWords) => term.labels],
    [DEFINITIONS, (term: ProcessTermWords) => term.definitions],
  ] as const) {
    const byLanguage = new Map<string, Record<string, string>>();
    for (const term of terms) {
      for (const { text, language } of texts(term)) {
        const bundle = byLanguage.get(language ?? "en") ?? {};
        bundle[term.iri] = text;
        byLanguage.set(language ?? "en", bundle);
      }
    }
    for (const [language, bundle] of byLanguage) i18n.addResourceBundle(language, namespace, bundle, true, true);
  }
}

/** Reads the words once; a failed read rejects, for a caller that can't go on without them, and the next call tries again. */
export function requireProcessTerms(): Promise<void> {
  loading ??= requestJson({ method: "GET", path: "/content/process-terms", decodeResponse: readProcessTerms })
    .then(holdProcessTerms)
    .catch((error: unknown) => {
      loading = null;
      throw error;
    });
  return loading;
}

/** `requireProcessTerms` for a caller that shows a term's IRI until its words arrive. */
export function loadProcessTerms(): Promise<void> {
  return requireProcessTerms().catch(() => undefined);
}

/** A term's label in `t`'s language, such as "document" for `dmwp:Document`; `null` until the words arrive or for a term the ontology doesn't name. */
export function termLabel(iri: string, t: TFunction): string | null {
  return lookup(iri, LABELS, t);
}

/** A term's definition in `t`'s language; `null` as for `termLabel`. */
export function termDefinition(iri: string, t: TFunction): string | null {
  return lookup(iri, DEFINITIONS, t);
}

/** The class of an item kind (`workflow` → `dmwp:Workflow`); `null` for a kind the ontology has no class for, or before the words arrive. */
export function itemKindClass(kind: string): string | null {
  return kindClasses.itemKinds[kind] ?? null;
}

/** The class of a step definition kind (`human` → `dmwp:HumanStepDefinition`); `null` as for `itemKindClass`. */
export function stepKindClass(kind: string): string | null {
  return kindClasses.stepKinds[kind] ?? null;
}

/** What a step kind alone implies on the two step axes; `null` before the words arrive. */
export function stepKindAxes(kind: string): KindAxes | null {
  return kindClasses.stepKindAxes[kind] ?? null;
}

/** An item kind by its class's label; a kind the ontology has no class for, such as a corpus, by its own word. */
export function itemKindText(kind: string, t: TFunction): string {
  const iri = itemKindClass(kind);
  return iri === null ? t(`reviews.kinds.${kind}`, { defaultValue: sentenceCase(spaced(kind)) }) : classText(iri, t);
}

/** A class or property of the ontology by its label; without one, by its local name in words. */
export function classText(iri: string, t: TFunction): string {
  return sentenceCase(termLabel(iri, t) ?? className(iriLocalName(iri)));
}

export function sentenceCase(words: string): string {
  return words.charAt(0).toLocaleUpperCase() + words.slice(1);
}

/** A project's class by its local name: `AnnualReport` → "Annual report", `review_comment` → "Review comment". */
function className(name: string): string {
  const words = spaced(name).replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  const [first = "", ...rest] = words.split(" ");
  return [first, ...rest.map((word) => (/^[A-Z][a-z]/.test(word) ? word.toLowerCase() : word))].join(" ");
}

/** `review_comment` → "review comment". */
export function spaced(name: string): string {
  return name.replaceAll("_", " ");
}

function lookup(iri: string, namespace: string, t: TFunction): string | null {
  const text = t(iri, { ...BY_IRI, ns: namespace, defaultValue: "" });
  return text === "" ? null : text;
}
